/**
 * Products service.
 *
 * Reads Firestore via the web SDK. Every function keeps the signature and
 * return shape it had in mock mode, so no UI component changes when the data
 * source does — set REACT_APP_USE_MOCK_DATA=true to fall back to /src/data.
 *
 * Endpoints in /src/config/api.config.js are legacy: there is no REST tier.
 */
import { mockProducts } from "@/data/mock-products";
import { applyProductFilters } from "@/utilities/filters";
import { USE_MOCK_DATA } from "@/lib/firebase";
import { getPublished, getPublishedBySlug } from "@/lib/firestore-data";

const delay = (ms = 60) => new Promise((res) => setTimeout(res, ms));

/** Published products, from Firestore or the fixtures. */
async function source() {
    if (USE_MOCK_DATA) {
        await delay();
        return mockProducts;
    }
    return getPublished("products");
}

export async function getProducts(filters = {}) {
    // applyProductFilters stays the single implementation of search/sort/paging
    // — Firestore cannot express its substring search, so it runs in memory.
    return applyProductFilters(await source(), filters);
}

export async function getFeaturedProducts(limit = 6) {
    const products = await source();
    return products.filter((p) => p.featured && p.status === "published").slice(0, limit);
}

export async function getProductBySlug(slug) {
    if (USE_MOCK_DATA) {
        await delay();
        return mockProducts.find((p) => p.slug === slug && p.status === "published") || null;
    }
    return getPublishedBySlug("products", slug);
}

export async function getRelatedProducts(productId, limit = 3) {
    const products = await source();
    const origin = products.find((p) => p.id === productId);
    if (!origin) return [];

    const scored = products
        .filter((p) => p.id !== productId && p.status === "published")
        .map((p) => {
            let score = 0;
            if (p.categoryId === origin.categoryId) score += 3;
            if (p.containerType === origin.containerType) score += 2;
            if (p.size === origin.size) score += 1;
            const overlap = (p.applications || []).filter((a) =>
                (origin.applications || []).includes(a),
            ).length;
            score += overlap;
            return { p, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);

    return scored.map((s) => s.p);
}

export async function getAllProductSlugs() {
    const products = await source();
    return products.filter((p) => p.status === "published").map((p) => p.slug);
}
